import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apiCall } from '../../../utils/apiClient';
import { Service, Addon } from './types/booking.types';

interface CancelServiceModalProps {
  services: Service[];
  bookingId: number;
  userId: number;
  bookingSubtotal: number;
  bookingDiscount: number;
  bookingFinalAmount: number;
  isFullCancellation?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

interface AddonDetail {
  addon: Addon;
  baseAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  totalAmount: number;
}

interface ServiceRefundDetail {
  service: Service;
  addons: Addon[];
  addonDetails: AddonDetail[];
  serviceBaseAmount: number;
  serviceTotalAmount: number;
  serviceCgstAmount: number;
  serviceSgstAmount: number;
  totalAddonBaseAmount: number;
  totalAddonAmount: number;
  totalAddonCgstAmount: number;
  totalAddonSgstAmount: number;
  subtotalBeforeGst: number;
  cgstRate: number;
  sgstRate: number;
  totalCgstAmount: number;
  totalSgstAmount: number;
  totalGstAmount: number;
  isAdditionalService: boolean;
  proportionalDiscount: number;
  bookingLevelAdjustment: number;
  finalRefundAmount: number;
}

interface PreviewSnapshot {
  booking_status: string;
  payment_status: string;
  subtotal_amount: number;
  discount_amount: number;
  coupon_code: string | null;
  coupon_valid: boolean;
  cgst_amount: number;
  sgst_amount: number;
  final_amount: number;
  paid_amount: number;
  due_amount: number;
  additional_approved_total: number;
  adjustments_total: number;
}

interface PreviewImpact {
  cancelled_subtotal: number;
  cancelled_discount: number;
  cancelled_cgst: number;
  cancelled_sgst: number;
  cancelled_total: number;
  discount_removed: number;
  coupon_after_valid: boolean;
}

interface PreviewSettlement {
  financial_outcome: string;
  refund_amount: number;
  extra_payable_amount: number;
}

interface CancellationPreviewData {
  before: PreviewSnapshot;
  impact: PreviewImpact;
  after: PreviewSnapshot;
  settlement: PreviewSettlement;
}

interface CancellationServicePayload {
  service_id: number | string;
  service_source: 'booking_service' | 'additional_service';
}

interface CancellationPayload {
  booking_id: number;
  reason?: string;
  notes?: string | null;
  services?: CancellationServicePayload[];
}

interface CancellationPreviewPayload {
  booking_id: number;
  services: CancellationServicePayload[];
}

const toNumber = (value: number | string | null | undefined) => {
  const parsed = parseFloat(String(value ?? 0));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';
const toText = (value: unknown, fallback = '-') => {
  if (!hasValue(value)) return fallback;
  return String(value);
};
const toBool = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  }
  return fallback;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const normalizeCancellationPreview = (payload: unknown): CancellationPreviewData | null => {
  const raw = asRecord(payload);
  if (Object.keys(raw).length === 0) return null;

  const hasStructuredPreview =
    raw.before !== undefined ||
    raw.impact !== undefined ||
    raw.after !== undefined ||
    raw.settlement !== undefined;
  if (!hasStructuredPreview) return null;

  const mapSnapshot = (value: unknown): PreviewSnapshot => {
    const section = asRecord(value);
    return {
      booking_status: toText(section.booking_status, '-'),
      payment_status: toText(section.payment_status, '-'),
      subtotal_amount: toNumber(section.subtotal_amount as number | string | null | undefined),
      discount_amount: toNumber(section.discount_amount as number | string | null | undefined),
      coupon_code: hasValue(section.coupon_code) ? String(section.coupon_code) : null,
      coupon_valid: toBool(section.coupon_valid, false),
      cgst_amount: toNumber(section.cgst_amount as number | string | null | undefined),
      sgst_amount: toNumber(section.sgst_amount as number | string | null | undefined),
      final_amount: toNumber(section.final_amount as number | string | null | undefined),
      paid_amount: toNumber(section.paid_amount as number | string | null | undefined),
      due_amount: toNumber(section.due_amount as number | string | null | undefined),
      additional_approved_total: toNumber(section.additional_approved_total as number | string | null | undefined),
      adjustments_total: toNumber(section.adjustments_total as number | string | null | undefined),
    };
  };

  const mapImpact = (value: unknown): PreviewImpact => {
    const section = asRecord(value);
    return {
      cancelled_subtotal: toNumber(section.cancelled_subtotal as number | string | null | undefined),
      cancelled_discount: toNumber(section.cancelled_discount as number | string | null | undefined),
      cancelled_cgst: toNumber(section.cancelled_cgst as number | string | null | undefined),
      cancelled_sgst: toNumber(section.cancelled_sgst as number | string | null | undefined),
      cancelled_total: toNumber(section.cancelled_total as number | string | null | undefined),
      discount_removed: toNumber(section.discount_removed as number | string | null | undefined),
      coupon_after_valid: toBool(section.coupon_after_valid, false),
    };
  };

  const mapSettlement = (value: unknown): PreviewSettlement => {
    const section = asRecord(value);
    return {
      financial_outcome: toText(section.financial_outcome, '-'),
      refund_amount: toNumber(section.refund_amount as number | string | null | undefined),
      extra_payable_amount: toNumber(section.extra_payable_amount as number | string | null | undefined),
    };
  };

  return {
    before: mapSnapshot(raw.before),
    impact: mapImpact(raw.impact),
    after: mapSnapshot(raw.after),
    settlement: mapSettlement(raw.settlement),
  };
};

const formatCurrency = (value: number) => `Rs ${value.toFixed(2)}`;

const isServiceCancelled = (service: Service) => service.status === 'cancelled' || service.status === 'completed';

const CancelServiceModal: React.FC<CancelServiceModalProps> = ({
  services,
  bookingId,
  userId,
  bookingSubtotal,
  bookingDiscount,
  bookingFinalAmount,
  isFullCancellation = false,
  onClose,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<CancellationPreviewData | null>(null);

  const handleModalClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  const servicesToCancel = useMemo(
    () => (Array.isArray(services) ? services.filter((service) => !isServiceCancelled(service)) : []),
    [services]
  );

  const bookingLevelMultiplier = useMemo(() => {
    if (bookingSubtotal <= 0 || bookingFinalAmount <= 0) return 1;
    return bookingFinalAmount / bookingSubtotal;
  }, [bookingFinalAmount, bookingSubtotal]);

  const shouldApplyBookingLevelRatio = useMemo(() => {
    const hasServiceLevelPricing = servicesToCancel.some((service) => {
      const addons = Array.isArray(service.addons) ? service.addons : [];
      const serviceHasDetailedFields =
        hasValue(service.total_amount) ||
        hasValue(service.discount_amount) ||
        hasValue(service.cgst_amount) ||
        hasValue(service.sgst_amount);

      const addonHasDetailedFields = addons.some(
        (addon) => hasValue(addon.total_amount) || hasValue(addon.cgst_amount) || hasValue(addon.sgst_amount)
      );

      return serviceHasDetailedFields || addonHasDetailedFields;
    });

    return !hasServiceLevelPricing && bookingDiscount <= 0 && Math.abs(bookingLevelMultiplier - 1) > 0.0001;
  }, [servicesToCancel, bookingDiscount, bookingLevelMultiplier]);

  const serviceRefundDetails = useMemo<ServiceRefundDetail[]>(() => {
    return servicesToCancel.map((service) => {
      const addons = Array.isArray(service.addons) ? service.addons : [];

      const serviceBaseAmount =
        service.amount !== undefined && service.amount !== null
          ? toNumber(service.amount)
          : toNumber(service.rate) * toNumber(service.quantity);
      const serviceTotalAmount = hasValue(service.total_amount)
        ? toNumber(service.total_amount)
        : serviceBaseAmount;

      const cgstRate = toNumber(service.cgst_rate ?? 0);
      const sgstRate = toNumber(service.sgst_rate ?? 0);
      const serviceCgstAmount = hasValue(service.cgst_amount)
        ? toNumber(service.cgst_amount)
        : cgstRate > 0
        ? (serviceBaseAmount * cgstRate) / 100
        : 0;
      const serviceSgstAmount = hasValue(service.sgst_amount)
        ? toNumber(service.sgst_amount)
        : sgstRate > 0
        ? (serviceBaseAmount * sgstRate) / 100
        : 0;

      const addonDetails = addons.map((addon) => {
        const addonBaseAmount =
          addon.amount !== undefined && addon.amount !== null
            ? toNumber(addon.amount)
            : toNumber(addon.rate) * toNumber(addon.quantity);
        const addonCgstAmount = hasValue(addon.cgst_amount) ? toNumber(addon.cgst_amount) : 0;
        const addonSgstAmount = hasValue(addon.sgst_amount) ? toNumber(addon.sgst_amount) : 0;
        const addonTotalAmount = hasValue(addon.total_amount)
          ? toNumber(addon.total_amount)
          : addonBaseAmount + addonCgstAmount + addonSgstAmount;

        return {
          addon,
          baseAmount: addonBaseAmount,
          cgstAmount: addonCgstAmount,
          sgstAmount: addonSgstAmount,
          totalAmount: addonTotalAmount,
        };
      });

      const totalAddonBaseAmount = addonDetails.reduce((sum, item) => sum + item.baseAmount, 0);
      const totalAddonAmount = addonDetails.reduce((sum, item) => sum + item.totalAmount, 0);
      const totalAddonCgstAmount = addonDetails.reduce((sum, item) => sum + item.cgstAmount, 0);
      const totalAddonSgstAmount = addonDetails.reduce((sum, item) => sum + item.sgstAmount, 0);
      const subtotalBeforeGst = serviceBaseAmount + totalAddonBaseAmount;
      const totalCgstAmount = serviceCgstAmount + totalAddonCgstAmount;
      const totalSgstAmount = serviceSgstAmount + totalAddonSgstAmount;
      const totalGstAmount = totalCgstAmount + totalSgstAmount;
      const grossTotalBeforeDiscount = serviceTotalAmount + totalAddonAmount;

      const isAdditionalService = service.is_additional === '1' || service.is_additional === true;

      let proportionalDiscount = hasValue(service.discount_amount) ? toNumber(service.discount_amount) : 0;
      if (!isAdditionalService && proportionalDiscount === 0 && bookingSubtotal > 0 && bookingDiscount > 0) {
        const serviceProportionOfBooking = subtotalBeforeGst / bookingSubtotal;
        proportionalDiscount = bookingDiscount * serviceProportionOfBooking;
      }

      let finalRefundAmount = grossTotalBeforeDiscount - proportionalDiscount;
      let bookingLevelAdjustment = 0;
      if (shouldApplyBookingLevelRatio) {
        finalRefundAmount = grossTotalBeforeDiscount * bookingLevelMultiplier;
        bookingLevelAdjustment = grossTotalBeforeDiscount - finalRefundAmount;
      }

      return {
        service,
        addons,
        addonDetails,
        serviceBaseAmount,
        serviceTotalAmount,
        serviceCgstAmount,
        serviceSgstAmount,
        totalAddonBaseAmount,
        totalAddonAmount,
        totalAddonCgstAmount,
        totalAddonSgstAmount,
        subtotalBeforeGst,
        cgstRate,
        sgstRate,
        totalCgstAmount,
        totalSgstAmount,
        totalGstAmount,
        isAdditionalService,
        proportionalDiscount,
        bookingLevelAdjustment,
        finalRefundAmount,
      };
    });
  }, [servicesToCancel, bookingSubtotal, bookingDiscount, shouldApplyBookingLevelRatio, bookingLevelMultiplier]);

  const cancellationEndpoint = 'admin/booking/cancellation';
  const cancellationPreviewEndpoint = 'admin/booking/cancellation/preview';
  const selectedServicesPayload = useMemo<CancellationServicePayload[]>(
    () =>
      serviceRefundDetails.map((detail) => ({
        service_id: detail.service.id,
        service_source: detail.isAdditionalService ? 'additional_service' : 'booking_service',
      })),
    [serviceRefundDetails]
  );

  const buildCancellationPayload = (): CancellationPayload => {
    const payload: CancellationPayload = {
      booking_id: bookingId,
    };

    if (!isFullCancellation) {
      payload.services = selectedServicesPayload;
    }

    payload.reason = reason;
    payload.notes = notes.trim() ? notes.trim() : null;

    return payload;
  };

  const buildCancellationPreviewPayload = (): CancellationPreviewPayload => ({
    booking_id: bookingId,
    services: selectedServicesPayload,
  });

  const fetchCancellationPreview = async () => {
    if (!bookingId) return;

    if (selectedServicesPayload.length === 0) {
      setPreviewData(null);
      setPreviewError('No selected services found for preview');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const res = await apiCall({
        endpoint: cancellationPreviewEndpoint,
        method: 'POST',
        data: buildCancellationPreviewPayload(),
      });

      if (res && (res.status === 200 || res.success === true)) {
        const primaryData =
          (res.data as Record<string, any> | null)?.preview ??
          (res.data as Record<string, any> | null)?.data ??
          res.data;
        const normalized = normalizeCancellationPreview(primaryData);

        if (normalized) {
          setPreviewData(normalized);
        } else {
          setPreviewData(null);
          setPreviewError('Cancellation preview data is unavailable');
        }
        return;
      }

      setPreviewData(null);
      setPreviewError(res?.message || 'Failed to load cancellation preview');
    } catch (err) {
      console.error('Error loading cancellation preview:', err);
      setPreviewData(null);
      setPreviewError('Failed to load cancellation preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    fetchCancellationPreview();
  }, [bookingId, isFullCancellation, selectedServicesPayload]);

  const cancellationReasons = [
    'Customer Request',
    'Service Quality Issues',
    'Schedule Mismatch',
    'Double Booking',
    'Customer Not Available',
    'Other',
  ];

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason) {
      toast.error('Please select a reason for cancellation');
      return;
    }

    if (serviceRefundDetails.length === 0) {
      toast.error('No cancellable services found');
      return;
    }

    setLoading(true);

    try {
      if (!isFullCancellation && selectedServicesPayload.length === 0) {
        toast.error('No selected services found for partial cancellation');
        return;
      }

      const payload = buildCancellationPayload();

      const result = await apiCall({
        endpoint: cancellationEndpoint,
        method: 'POST',
        data: payload,
      });

      if (result && (result.status === 200 || result.status === 201 || result.success === true)) {
        toast.success(
          isFullCancellation
            ? 'Full booking cancelled successfully'
            : 'Selected services cancelled successfully'
        );
        onClose();
        onSubmit();
      } else {
        toast.error(result?.message || 'Failed to cancel booking/services');
      }
    } catch (err) {
      console.error('Error cancelling booking/services:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-2"
      onClick={handleModalClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-base font-bold text-red-700">Cancel Service(s) and Addons</h2>
          <button
            type="button"
            onClick={handleModalClose}
            aria-label="Close modal"
            className="z-10 inline-flex h-8 w-8 items-center justify-center rounded text-xl font-semibold leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            X
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
          <div className="rounded border border-red-200 bg-red-50 p-3">
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Services to Cancel ({serviceRefundDetails.length})</h3>
            <div className="space-y-2 text-sm">
              {serviceRefundDetails.map((detail, index) => (
                <div key={`${detail.service.id}-${index}`} className="rounded border border-red-100 bg-white px-3 py-2">
                  <p className="font-medium text-gray-800">{detail.service.service_name}</p>
                  <p className="text-xs text-gray-600">
                    Qty: {detail.service.quantity} {detail.service.unit} | Rate: Rs {detail.service.rate}
                  </p>
                  {detail.addons.length > 0 && (
                    <p className="text-xs text-gray-500">Addons: {detail.addons.map((addon) => addon.addon_name).join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded border border-purple-200 bg-purple-50 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                {isFullCancellation ? 'Full Booking Cancellation Preview' : 'Selected Services Cancellation Preview'}
              </h3>
              {previewLoading && <span className="text-xs text-purple-700">Loading...</span>}
            </div>

            {previewData ? (
              <div className="space-y-3 text-xs">
                <div className="rounded border border-purple-100 bg-white p-3">
                  <p className="mb-2 font-semibold text-purple-900">Before</p>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    <p className="text-gray-700">Booking Status: <span className="font-semibold">{previewData.before.booking_status}</span></p>
                    <p className="text-gray-700">Payment Status: <span className="font-semibold">{previewData.before.payment_status}</span></p>
                    <p className="text-gray-700">Coupon Valid: <span className="font-semibold">{previewData.before.coupon_valid ? 'Yes' : 'No'}</span></p>
                    <p className="text-gray-700">Subtotal: <span className="font-semibold">{formatCurrency(previewData.before.subtotal_amount)}</span></p>
                    <p className="text-gray-700">Discount: <span className="font-semibold">{formatCurrency(previewData.before.discount_amount)}</span></p>
                    <p className="text-gray-700">CGST: <span className="font-semibold">{formatCurrency(previewData.before.cgst_amount)}</span></p>
                    <p className="text-gray-700">SGST: <span className="font-semibold">{formatCurrency(previewData.before.sgst_amount)}</span></p>
                    <p className="text-gray-700">Final: <span className="font-semibold">{formatCurrency(previewData.before.final_amount)}</span></p>
                    <p className="text-gray-700">Paid: <span className="font-semibold">{formatCurrency(previewData.before.paid_amount)}</span></p>
                    <p className="text-gray-700">Due: <span className="font-semibold">{formatCurrency(previewData.before.due_amount)}</span></p>
                    <p className="text-gray-700">Coupon Code: <span className="font-semibold">{previewData.before.coupon_code || '-'}</span></p>
                  </div>
                </div>

                <div className="rounded border border-purple-100 bg-white p-3">
                  <p className="mb-2 font-semibold text-purple-900">Impact</p>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    <p className="text-gray-700">Cancelled Subtotal: <span className="font-semibold">{formatCurrency(previewData.impact.cancelled_subtotal)}</span></p>
                    <p className="text-gray-700">Cancelled Discount: <span className="font-semibold">{formatCurrency(previewData.impact.cancelled_discount)}</span></p>
                    <p className="text-gray-700">Cancelled CGST: <span className="font-semibold">{formatCurrency(previewData.impact.cancelled_cgst)}</span></p>
                    <p className="text-gray-700">Cancelled SGST: <span className="font-semibold">{formatCurrency(previewData.impact.cancelled_sgst)}</span></p>
                    <p className="text-gray-700">Cancelled Total: <span className="font-semibold">{formatCurrency(previewData.impact.cancelled_total)}</span></p>
                    <p className="text-gray-700">Discount Removed: <span className="font-semibold">{formatCurrency(previewData.impact.discount_removed)}</span></p>
                    <p className="text-gray-700">Coupon Valid After: <span className="font-semibold">{previewData.impact.coupon_after_valid ? 'Yes' : 'No'}</span></p>
                  </div>
                </div>

                <div className="rounded border border-purple-100 bg-white p-3">
                  <p className="mb-2 font-semibold text-purple-900">After</p>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    <p className="text-gray-700">Booking Status: <span className="font-semibold">{previewData.after.booking_status}</span></p>
                    <p className="text-gray-700">Payment Status: <span className="font-semibold">{previewData.after.payment_status}</span></p>
                    <p className="text-gray-700">Coupon Valid: <span className="font-semibold">{previewData.after.coupon_valid ? 'Yes' : 'No'}</span></p>
                    <p className="text-gray-700">Subtotal: <span className="font-semibold">{formatCurrency(previewData.after.subtotal_amount)}</span></p>
                    <p className="text-gray-700">Discount: <span className="font-semibold">{formatCurrency(previewData.after.discount_amount)}</span></p>
                    <p className="text-gray-700">CGST: <span className="font-semibold">{formatCurrency(previewData.after.cgst_amount)}</span></p>
                    <p className="text-gray-700">SGST: <span className="font-semibold">{formatCurrency(previewData.after.sgst_amount)}</span></p>
                    <p className="text-gray-700">Final: <span className="font-semibold">{formatCurrency(previewData.after.final_amount)}</span></p>
                    <p className="text-gray-700">Paid: <span className="font-semibold">{formatCurrency(previewData.after.paid_amount)}</span></p>
                    <p className="text-gray-700">Due: <span className="font-semibold">{formatCurrency(previewData.after.due_amount)}</span></p>
                    <p className="text-gray-700">Additional Approved: <span className="font-semibold">{formatCurrency(previewData.after.additional_approved_total)}</span></p>
                    <p className="text-gray-700">Adjustments Total: <span className="font-semibold">{formatCurrency(previewData.after.adjustments_total)}</span></p>
                    <p className="text-gray-700">Coupon Code: <span className="font-semibold">{previewData.after.coupon_code || '-'}</span></p>
                  </div>
                </div>

                <div className="rounded border border-purple-100 bg-white p-3">
                  <p className="mb-2 font-semibold text-purple-900">Settlement</p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <p className="text-gray-700">Financial Outcome: <span className="font-semibold">{previewData.settlement.financial_outcome}</span></p>
                    <p className="text-gray-700">Refund Amount: <span className="font-semibold">{formatCurrency(previewData.settlement.refund_amount)}</span></p>
                    <p className="text-gray-700">Extra Payable: <span className="font-semibold">{formatCurrency(previewData.settlement.extra_payable_amount)}</span></p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-600">
                {previewLoading ? 'Fetching preview...' : previewError || 'Preview is not available right now.'}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-700">
              Reason for Cancellation <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason</option>
              {cancellationReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-700">Additional Notes (Optional)</label>
            <textarea
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              rows={3}
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="rounded border border-yellow-200 bg-yellow-50 p-3">
            <p className="text-xs text-yellow-800">
              <span className="font-semibold">Note:</span> This will cancel selected service(s) with their addons and create refund adjustments.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50"
            disabled={loading}
          >
            Keep Services
          </button>
          <button
            onClick={handleCancel}
            disabled={loading || !reason || serviceRefundDetails.length === 0}
            className="flex items-center rounded bg-red-600 px-6 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelServiceModal;

